import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { useAuth } from 'app/auth/hooks/useAuth';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { usePost, useFetch } from 'app/queryProvider';
import type {
  TeleassistancePreferredDate,
  TeleassistancePreferredTime,
  TExam,
} from 'app/_modules/pathways';

interface IExamsResponse {
  results: TExam[];
}

export const useTeleassistanceRequestForm = () => {
  const { t } = dynamicTranslations();
  const { meData } = useAuth();
  const queryClient = useQueryClient();
  const pathwayId = meData?.current_pathway?.id ?? '';

  const [examId, setExamId] = useState<string | null>(null);
  const [preferredDate, setPreferredDate] = useState<TeleassistancePreferredDate | null>(null);
  const [preferredTime, setPreferredTime] = useState<TeleassistancePreferredTime | null>(null);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data: examsData, isFetching: isLoadingExams } = useFetch<IExamsResponse>({
    queryKey: [['exams', 'teleassistance']],
    url: '?_filter={"modality":"teleassistance"}',
    resource: { name: 'exams' },
  });

  const exams = examsData?.results ?? [];

  const formTouched =
    examId !== null ||
    preferredDate !== null ||
    preferredTime !== null ||
    additionalDetails.trim().length > 0;

  const isFormComplete =
    examId !== null &&
    preferredDate !== null &&
    preferredTime !== null;

  const { mutate: submitRequest, isPending: isSubmitting } = usePost<
    {
      pathwayId: string;
      modality: 'teleassistance';
      examId: string;
      preferredDate: TeleassistancePreferredDate;
      preferredTime: TeleassistancePreferredTime;
      additionalDetails: string;
    },
    void
  >({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [['appointments', pathwayId]] });
        setIsSubmitted(true);
      },
      onError: () => {
        ToastNotifications.displayErrorNotification(t('teleassistance.form.toast.submitError'));
      },
    },
  });

  const handleSubmit = () => {
    if (!isFormComplete) return;
    submitRequest({
      resource: { name: 'appointments' },
      url: '',
      data: {
        pathwayId,
        modality: 'teleassistance',
        examId: examId!,
        preferredDate: preferredDate!,
        preferredTime: preferredTime!,
        additionalDetails: additionalDetails.trim(),
      },
    });
  };

  return {
    exams,
    isLoadingExams,
    examId,
    setExamId,
    preferredDate,
    setPreferredDate,
    preferredTime,
    setPreferredTime,
    additionalDetails,
    setAdditionalDetails,
    formTouched,
    isFormComplete,
    isSubmitting,
    isSubmitted,
    handleSubmit,
  };
};
